import { keyframes } from '@emotion/react';
import CloseIcon from '@mui/icons-material/Close';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import IconButton from '@mui/material/IconButton';
import { useTheme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import { RunnerResultCard } from '@/core/components';
import type { Runner } from '@/core/types';
import { calcNetTime, getFinishTime, getStartTime } from '@/core/types';

// トロフィーのバウンスアニメーション
const bounce = keyframes`
  0% {
    transform: scale(0) rotate(-10deg);
    opacity: 0;
  }
  50% {
    transform: scale(1.2) rotate(5deg);
  }
  70% {
    transform: scale(0.9) rotate(-3deg);
  }
  100% {
    transform: scale(1) rotate(0deg);
    opacity: 1;
  }
`;

// トロフィーのシャインエフェクト
const shine = keyframes`
  0% {
    background-position: -100% 0;
  }
  100% {
    background-position: 200% 0;
  }
`;

const textShine = keyframes`
  0% {
    background-position: 200% center;
  }
  100% {
    background-position: -200% center;
  }
`;

interface OnSiteRunnerConfirmModalProps {
  runner: Runner | null;
  open: boolean;
  loading?: boolean;
  onClose: () => void;
}

export function OnSiteRunnerConfirmModal({ runner, open, loading, onClose }: OnSiteRunnerConfirmModalProps) {
  const theme = useTheme();
  const gradient = theme.custom?.gradient || theme.palette.primary.main;

  const finishTime = runner ? getFinishTime(runner) : '';
  const netTime = runner ? calcNetTime(getStartTime(runner), finishTime) : '';

  return (
    <Dialog open={open} onClose={loading ? undefined : onClose} maxWidth="sm" fullWidth>
      {!loading && (
        <IconButton
          onClick={onClose}
          sx={{
            position: 'absolute',
            right: 8,
            top: 8,
            color: 'text.secondary',
          }}
        >
          <CloseIcon />
        </IconButton>
      )}

      <DialogContent sx={{ pt: 4, pb: 2 }}>
        {loading ? (
          /* ローディング中 */
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <CircularProgress size={48} sx={{ color: theme.palette.primary.main, mb: 2 }} />
            <Typography variant="body1" color="text.secondary">
              記録を確認しています...
            </Typography>
          </Box>
        ) : runner ? (
          <>
            {/* 完走確認 */}
            <Box sx={{ textAlign: 'center', mb: 3 }}>
              {/* トロフィー + シャインエフェクト */}
              <Box
                sx={{
                  display: 'inline-block',
                  position: 'relative',
                  animation: `${bounce} 0.6s ease-out`,
                }}
              >
                <EmojiEventsIcon
                  sx={{
                    fontSize: 56,
                    color: theme.palette.warning.main,
                    mb: 1,
                  }}
                />
                {/* シャインオーバーレイ */}
                <Box
                  sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    background: 'linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.4) 50%, transparent 100%)',
                    backgroundSize: '200% 100%',
                    animation: `${shine} 2s ease-in-out 0.6s infinite`,
                    pointerEvents: 'none',
                  }}
                />
              </Box>
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 700,
                  background: gradient,
                  backgroundClip: 'text',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                }}
              >
                {runner.fullName || 'ランナー'}さん
                <br />
                完走おめでとうございます！
              </Typography>
            </Box>

            {/* タイム表示 */}
            <RunnerResultCard bib={runner.bib} grossTime={finishTime} netTime={netTime} />

            {/* メッセージ */}
            <Box sx={{ mt: 3, textAlign: 'center' }}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                表示されたお名前に間違いがなければOKボタンを押して、画面の指示に従って操作を進めてください。
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  fontWeight: 600,
                  background: `linear-gradient(90deg, ${theme.palette.text.primary} 0%, ${theme.palette.text.primary} 40%, ${theme.palette.primary.main} 50%, ${theme.palette.text.primary} 60%, ${theme.palette.text.primary} 100%)`,
                  backgroundSize: '200% auto',
                  backgroundClip: 'text',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  animation: `${textShine} 3s ease-in-out infinite`,
                }}
              >
                以上でこちらでの対応は終了です。
                <br />
                お帰りの準備へお進みください。
              </Typography>
            </Box>

            {/* 注意書き */}
            <Typography
              variant="caption"
              sx={{
                display: 'block',
                mt: 2,
                textAlign: 'center',
                color: 'error.main',
              }}
            >
              ※ 表示されているのがご自身のお名前でない場合はスタッフにお申し付けください。
            </Typography>
          </>
        ) : (
          /* ランナーが見つからなかった場合 */
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography variant="body1" color="error.main">
              記録が見つかりませんでした
            </Typography>
          </Box>
        )}
      </DialogContent>

      {!loading && (
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button
            variant="contained"
            fullWidth
            onClick={onClose}
            sx={{
              background: gradient,
              color: '#fff',
              height: 48,
              fontWeight: 600,
              '&:hover': {
                background: gradient,
              },
            }}
          >
            OK
          </Button>
        </DialogActions>
      )}
    </Dialog>
  );
}
